import { normalizeCardStamp } from 'features/authorization/constants/authorizationChannels';
import { resolveAuthorizationCarrierCode } from 'features/authorization/helpers/authorizationCrossExpiryWarning';

export interface ExistingAuthorizationForWarning {
	createdAt: string;
	chargedAgainstOptionCode?: string | null;
	chargedAgainstOptionName: string;
}

export const isSameCalendarDay = (isoDate: string, reference: Date = new Date()): boolean => {
	const date = new Date(isoDate);
	if (Number.isNaN(date.getTime())) return false;

	return (
		date.getFullYear() === reference.getFullYear() &&
		date.getMonth() === reference.getMonth() &&
		date.getDate() === reference.getDate()
	);
};

const getFirstAuthorizationOfDay = (
	authorizations: ExistingAuthorizationForWarning[],
	reference: Date = new Date(),
): ExistingAuthorizationForWarning | null => {
	const todayAuthorizations = authorizations
		.filter((auth) => isSameCalendarDay(auth.createdAt, reference))
		.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime());

	return todayAuthorizations[0] ?? null;
};

export type MastercardSameDayWarningLevel = 'reminder' | 'critical';

export interface MastercardSameDayAuthorizationWarning {
	level: MastercardSameDayWarningLevel;
	title: string;
	message: string;
}

export const getMastercardSameDayAuthorizationWarning = (
	cardStamp: string,
	existingAuthorizations: ExistingAuthorizationForWarning[],
	selectedCarrierCode?: string | null,
	selectedCarrierName?: string | null,
	referenceDate: Date = new Date(),
): MastercardSameDayAuthorizationWarning | null => {
	if (normalizeCardStamp(cardStamp) !== 'mastercard') return null;
	if (!existingAuthorizations.length) return null;

	const firstTodayAuthorization = getFirstAuthorizationOfDay(existingAuthorizations, referenceDate);
	if (!firstTodayAuthorization) return null;

	const referenceCode = resolveAuthorizationCarrierCode(
		firstTodayAuthorization.chargedAgainstOptionCode,
		firstTodayAuthorization.chargedAgainstOptionName,
	);
	const referenceName = firstTodayAuthorization.chargedAgainstOptionName;

	const selectedCode = selectedCarrierCode
		? resolveAuthorizationCarrierCode(selectedCarrierCode, selectedCarrierName)
		: null;
	const selectedName = selectedCarrierName?.trim() || null;

	if (selectedCode && referenceCode && selectedCode === referenceCode) return null;

	if (selectedCode && referenceCode && selectedCode !== referenceCode) {
		return {
			level: 'critical',
			title: 'Aerolínea distinta — riesgo de ADM en BSP',
			message: `Esta transacción es Mastercard y ya tiene una autorización creada hoy contra ${referenceName}. Estás por autorizar contra ${
				selectedName ?? 'otra compañía aérea'
			}. Mastercard puede identificar esto como posible fraude y generar un ADM en BSP. Autorizá contra ${referenceName} o consultá con tu líder antes de continuar.`,
		};
	}

	return {
		level: 'reminder',
		title: 'Usá la misma aerolínea que hoy',
		message: `Esta transacción es Mastercard y ya tiene una autorización creada hoy contra ${referenceName}. Se recomienda autorizar contra la misma compañía aérea.`,
	};
};
